import { ChildMachineConfig, ParentState } from "@capone/checkout";

import { ActionTypes } from "./actions";
import {
  TravelerInformationState,
  TravelerInformationChildState,
} from "./types";
import { GuardTypes } from "./guards";
import { services } from "./services";
import { Event } from "../../../modules/book/state/events";

export const getMachineState = (config: ChildMachineConfig) => ({
  id: ParentState.activityTravelerInformation,
  initial: TravelerInformationState.loading,
  states: {
    [TravelerInformationState.loading]: {
      initial: TravelerInformationChildState.fetch,
      states: {
        [TravelerInformationChildState.fetch]: {
          invoke: {
            src: services.fetchUserPassengersService,
            onDone: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.loading}.${TravelerInformationChildState.route}`,
              actions: [
                ActionTypes.setUserTravelers,
                ActionTypes.setSingleDefaultTraveler,
              ],
            },
            onError: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.error}`,
              actions: ActionTypes.setTravelersError,
            },
          },
        },
        [TravelerInformationChildState.route]: {
          always: [
            // If single traveler has missing required fields, currentUserTraveler is set by setSingleDefaultTraveler
            // so we route to the form to edit them instead of the picker
            {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}`,
              cond: GuardTypes.hasCurrentUserTraveler,
            },
            {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}`,
              cond: GuardTypes.haveAnyTravelersLoaded,
            },
            {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}`,
            },
          ],
        },
      },
    },
    [TravelerInformationState.travelerPicker]: {
      initial: TravelerInformationChildState.idle,
      states: {
        [TravelerInformationChildState.idle]: {},
        [TravelerInformationChildState.delete]: {
          invoke: {
            src: services.deleteUserPassengersService,
            onDone: {
              actions: ActionTypes.handleDeleteTraveler,
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}.${TravelerInformationChildState.idle}`,
            },
            onError: {
              actions: ActionTypes.setTravelersError,
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.error}`,
            },
          },
        },
      },
      on: {
        [Event.SELECT_TRAVELER]: [
          {
            actions: ActionTypes.selectTraveler,
          },
        ],
        [Event.NEXT]: [
          // Both mobile and desktop must validate that all selected travelers have required fields
          {
            target: `#${config.parentMachineId}.${ParentState.contactInformation}`,
            cond: GuardTypes.allSelectedTravelersValidAndMobile,
          },
          {
            target:
              config.nextState ||
              `#${config.parentMachineId}.${ParentState.activityTravelerInformation}`,
            cond: GuardTypes.allSelectedTravelersHaveRequiredFields,
          },
        ],
      },
    },
    [TravelerInformationState.travelerForm]: {
      initial: TravelerInformationChildState.route,
      states: {
        [TravelerInformationChildState.route]: {
          always: [
            {
              cond: GuardTypes.hasCurrentUserTraveler,
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.edit}`,
            },
            {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.new}`,
            },
          ],
        },
        [TravelerInformationChildState.edit]: {
          invoke: {
            src: services.trackEditTravelers,
            onDone: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.idle}`,
            },
            onError: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.idle}`,
            },
          },
        },
        [TravelerInformationChildState.new]: {
          invoke: {
            src: services.trackNewTraveler,
            onDone: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.idle}`,
            },
            onError: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.idle}`,
            },
          },
        },
        [TravelerInformationChildState.idle]: {},
        [TravelerInformationChildState.update]: {
          invoke: {
            src: services.updateUserPassengerService,
            onDone: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}.${TravelerInformationChildState.idle}`,
              actions: [
                ActionTypes.setUserTravelers,
                ActionTypes.selectTraveler,
                ActionTypes.clearCurrentTraveler,
              ],
            },
            onError: {
              target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.error}`,
              actions: ActionTypes.setTravelersError,
            },
          },
        },
      },
      on: {
        [Event.UPDATE_TRAVELER]: {
          target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}.${TravelerInformationChildState.update}`,
        },
        [Event.ANSWER_BOOKING_QUESTIONS]: {
          actions: ActionTypes.setPerTravelerBookingQuestions,
        },
      },
    },
    [TravelerInformationState.error]: {},
  },
  on: {
    [Event.DELETE_TRAVELER]: {
      target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}.${TravelerInformationChildState.delete}`,
    },
    [Event.SET_CURRENT_TRAVELER]: {
      actions: ActionTypes.setCurrentTraveler,
    },
    [Event.CLEAR_TRAVELER_INFORMATION_ERROR]: {
      actions: ActionTypes.clearTravelerInformationError,
      target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}`,
    },
    [Event.OPEN_TRAVELER_PICKER]: {
      target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerPicker}`,
      actions: ActionTypes.clearCurrentTraveler,
    },

    [Event.OPEN_TRAVELER_FORM]: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}`,
    // [Event.OPEN_FORM_AND_SET_TRAVELER]: {
    //   actions: ActionTypes.setCurrentTraveler,
    //   target: `#${config.parentMachineId}.${ParentState.activityTravelerInformation}.${TravelerInformationState.travelerForm}`,
    // },
    [Event.PREVIOUS]: {
      target: config?.previousState || ParentState.contactInformation,
    },
  },
});
